import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";

/**
 * Confirmation dialog with comment component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isShow - Flag indicating whether the dialog is shown or not.
 * @param {Function} props.onSubmit - The function to be called when the submit button is clicked.
 * @param {Object} props.modalProps - The properties of the modal.
 * @param {string} props.modalProps.title - The title of the modal.
 * @param {string} props.modalProps.commentLabel - The label for the comment input field.
 * @param {string} props.modalProps.commentError - The error message for the comment input field.
 * @param {string} props.modalProps.buttonSubmitText - The text for the submit button.
 * @param {Function} props.onClose - The function to be called when the close button is clicked.
 * @param {string} props.comment - The comment value.
 * @param {Function} props.setComment - The function to set the comment value.
 * @param {boolean} props.isLoading - Flag indicating whether the component is in loading state or not.
 * @returns {JSX.Element} The ConfirmationDialogWithComment component.
 */
const ConfirmationDialogWithComment = ({ isShow, onSubmit, modalProps, onClose, comment, setComment, isLoading }) => {
  const [showError, setShowError] = useState(false);
  const checkAndSubmit = () => {
    if (isLoading) return;
    if (comment.trim() === "") {
      setShowError(true);
    } else {
      onSubmit();
    }
  };

  return (
    <Modal show={isShow} onHide={onClose}>
      <Modal.Body>
        <h3>{modalProps?.title}</h3>
        <Form>
          <Form.Group controlId="cancelReason">
            <Form.Label
              style={{
                fontSize: "1rem",
                marginTop: "1rem"
              }}
            >
              {modalProps?.commentLabel}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              required
              isInvalid={showError && comment.trim() === ""}
              value={comment}
              onChange={e => {
                setComment(e.target.value);
                setShowError(false);
              }}
            />
            <Form.Control.Feedback
              type="invalid"
              style={{
                fontSize: "1rem"
              }}
            >
              {modalProps?.commentError}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={checkAndSubmit} disabled={isLoading}>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <div
                style={{
                  height: "15px",
                  width: "15px",
                  marginRight: "10px",
                  transform: "scale(0.5)",
                  border: "2px solid #f3f3f3",
                  borderTopColor: "transparent",
                  borderRightColor: "transparent",
                  animation: "spin 1s linear infinite",
                  borderRadius: "50%"
                }}
              />
              Loading
            </div>
          ) : (
            <>{modalProps.buttonSubmitText}</>
          )}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <div
                style={{
                  height: "15px",
                  width: "15px",
                  marginRight: "10px",
                  transform: "scale(0.5)",
                  border: "2px solid #f3f3f3",
                  borderTopColor: "transparent",
                  borderRightColor: "transparent",
                  animation: "spin 1s linear infinite",
                  borderRadius: "50%"
                }}
              />
              Loading
            </div>
          ) : (
            "Close"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmationDialogWithComment;
